import Keycloak, { KeycloakInitOptions } from "keycloak-js";
import i18n from "../i18n";
import logger from "../utils/logging";
import { isMobileSafari, supportsSessionStatusIframe } from "../utils/browserDetection";
import { AuthenticationInitError, AuthenticationTimeoutError } from "../errors";
import { Environment } from "../init/config";

export type User = {
    token: string;
    refreshToken?: string;
    username?: string;
    email?: string;
    roles?: string[];
};

export type AuthError = {
    type: "auth" | "general";
    title: string;
    message: string;
    errorCode?: string;
    details?: string;
    requiresReauth?: boolean;
};

type KeycloakConfig = {
    url: string;
    realm: string;
    clientId: string;
};

type PingResult = { reachable: true } | { reachable: false; error: string };

export function buildKeycloakConfig(keycloakBaseUrl: string): KeycloakConfig {
    return {
        url: keycloakBaseUrl,
        realm: "users",
        clientId: "sweap-pdf-form-filler",
    };
}

export const AUTH_REDIRECT_PATH = "/oauth2/callback";
export const AUTH_REDIRECT_STORAGE_KEY = "sweap_keycloak_preAuthRedirect";

export default class UserAuthService {
    private readonly keycloak: Keycloak;
    private readonly config: KeycloakConfig;
    private readonly environment: Environment;

    private authListeners: ((user: User | undefined) => void)[] = [];
    private errorListeners: ((error: AuthError) => void)[] = [];
    private initListeners: ((initialized: boolean) => void)[] = [];
    private initialized = false;
    private initPromise: Promise<boolean> | null = null;

    constructor(config: KeycloakConfig, environment: Environment) {
        this.config = config;
        this.environment = environment;

        this.keycloak = new Keycloak({
            url: config.url,
            realm: config.realm,
            clientId: config.clientId,
        });

        // Set up token refresh
        this.keycloak.onTokenExpired = () => {
            this.refreshToken();
        };

        // Set up authentication state change listeners
        this.keycloak.onAuthSuccess = () => {
            logger.debug("Keycloak: Authentication successful");
            this.notifyAuthListeners();
        };

        this.keycloak.onAuthLogout = () => {
            logger.debug("Keycloak: User logged out");
            this.notifyAuthListeners();
        };

        this.keycloak.onAuthRefreshSuccess = () => {
            logger.debug("Keycloak: Token refresh successful");
            this.notifyAuthListeners();
        };

        this.keycloak.onAuthRefreshError = () => {
            logger.debug("Keycloak: Token refresh failed");
            this.notifyAuthListeners();
        };

        this.keycloak.onAuthError = (errorData) => {
            const debugInfo = this.buildKeycloakDebugInfo(errorData);

            logger.error("Keycloak: Authentication error with debug info", debugInfo);

            this.notifyAuthListeners();
        };
    }

    async init(): Promise<boolean> {
        // Return cached Promise if initialization is already in progress or completed
        if (this.initPromise) {
            logger.debug("Auth service initialization already in progress, returning cached Promise");
            return this.initPromise;
        }

        // Create and cache the initialization Promise
        this.initPromise = this.performInitialization();

        try {
            return await this.initPromise;
        } finally {
            // Always clear cache, whether success or error
            this.initPromise = null;
        }
    }

    private async performInitialization(): Promise<boolean> {
        if (this.initialized) {
            logger.debug("Auth service already initialized, returning current state", {
                authenticated: this.keycloak.authenticated || false,
            });
            // Always notify listeners in case new ones were added after initialization
            this.notifyInitListeners();
            return this.keycloak.authenticated || false;
        }

        try {
            const isMobile = isMobileSafari();
            const supportsIframe = supportsSessionStatusIframe();

            logger.debug("Initializing Keycloak...", {
                isMobileSafari: isMobile,
                supportsIframe: supportsIframe,
            });

            // Configure initialization based on browser capabilities
            const initConfig: KeycloakInitOptions = {
                pkceMethod: "S256",
                flow: "standard",
            };

            if (isMobile) {
                // Mobile Safari: Skip automatic SSO check to prevent unwanted redirects
                logger.debug("Mobile Safari detected, skipping automatic SSO check");
            } else {
                // Desktop browsers: Use check-sso for silent authentication
                initConfig.onLoad = "check-sso";
                initConfig.silentCheckSsoRedirectUri = `${window.location.origin}/silent-check-sso.html`;
                initConfig.checkLoginIframe = supportsIframe;
                initConfig.checkLoginIframeInterval = 5; // Check every 5 seconds
                // can be activated when there is time (mheicke)
                // initConfig.silentCheckSsoFallback = true;
                // initConfig.messageReceiveTimeout = 1000;
                logger.debug("Desktop browser detected, using check-sso mode");
            }

            const authenticated = await this.keycloak.init(initConfig);

            this.initialized = true;
            this.notifyInitListeners();

            logger.debug("Keycloak initialization completed", {
                authenticated,
                token: !!this.keycloak.token,
                refreshToken: !!this.keycloak.refreshToken,
                tokenParsed: !!this.keycloak.tokenParsed,
                timeSkew: this.keycloak.timeSkew,
                refreshTokenParsed: !!this.keycloak.refreshTokenParsed,
                sessionState: (this.keycloak as any).sessionId,
                subject: this.keycloak.subject,
                isLoggedInCheck: this.keycloak.authenticated,
            });

            if (authenticated) {
                logger.debug("User authenticated via Keycloak");
                this.notifyAuthListeners();
            } else {
                logger.debug("User not authenticated");
            }

            return authenticated;
        } catch (error: any) {
            // Handle Keycloak's "already initialized" error gracefully
            if (error instanceof Error && error.message.includes("can only be initialized once")) {
                logger.warn("Keycloak already initialized (likely React StrictMode), returning current state", {
                    authenticated: this.keycloak.authenticated || false,
                    hasToken: !!this.keycloak.token,
                });

                // Mark as initialized and notify listeners
                this.initialized = true;
                this.notifyInitListeners();

                return this.keycloak.authenticated || false;
            }

            // Enhanced error logging for initialization failures
            const debugInfo = this.buildKeycloakDebugInfo(error);

            if (error?.error == "Timeout when waiting for 3rd party check iframe message.") {
                throw new AuthenticationTimeoutError({ context: debugInfo });
            }

            // Re-throw real errors notifyErrorListeners not needed, since catch clause of init will fetch this (mheicke)
            throw new AuthenticationInitError({
                cause: error instanceof Error ? error : undefined,
                context: debugInfo,
            });
        }
    }

    // TODO: can be used to check, if keycloak is reachable or not on AuthenticationTimeoutError (mheicke)
    async pingKeycloak(timeoutMs = 3000): Promise<PingResult> {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), timeoutMs);

        const url = `${this.config.url.replace(/\/$/, "")}/realms/${encodeURIComponent(this.config.realm)}/.well-known/openid-configuration`;

        try {
            const response = await fetch(url, { method: "GET", signal: controller.signal });

            if (!response.ok) {
                return { reachable: false, error: `HTTP ${response.status}` };
            }

            return { reachable: true };
        } catch (err: any) {
            const message = err.name === "AbortError" ? "Timeout" : err.message || "Unknown error";
            return { reachable: false, error: message };
        } finally {
            clearTimeout(timeout);
        }
    }

    isInitialized(): boolean {
        return this.initialized;
    }

    isLoggedIn(): boolean {
        const authenticated = this.keycloak.authenticated || false;
        logger.debug("isLoggedIn() called", {
            authenticated,
            initialized: this.initialized,
            hasToken: !!this.keycloak.token,
            subject: this.keycloak.subject,
        });
        return authenticated;
    }

    async login(redirectUrl?: string): Promise<void> {
        // Store redirect location for post-auth redirect
        const redirectTo = redirectUrl || window.location.pathname + window.location.search;
        sessionStorage.setItem(AUTH_REDIRECT_STORAGE_KEY, redirectTo);
        if (redirectTo == undefined || redirectTo == "undefined") {
            logger.warn(
                "Setting redirectTo for Oauth callback to undefined or string 'undefined' " +
                    redirectTo +
                    " (type " +
                    typeof redirectTo +
                    ")",
            );
        }

        await this.keycloak.login({
            redirectUri: window.location.origin + AUTH_REDIRECT_PATH,
        });
    }

    logout(): void {
        // Clean up any stored redirect
        sessionStorage.removeItem(AUTH_REDIRECT_STORAGE_KEY);

        this.keycloak.logout({
            redirectUri: window.location.origin + AUTH_REDIRECT_PATH, // maybe only: redirectUri: window.location.origin
        });
    }

    async refreshToken(): Promise<boolean> {
        try {
            const refreshed = await this.keycloak.updateToken(30);
            if (refreshed) {
                logger.debug("Token refreshed");
                this.notifyAuthListeners();
            }
            return refreshed;
        } catch (error) {
            logger.error("Failed to refresh token:", error);
            return false;
        }
    }

    getUser(): User | undefined {
        if (!this.keycloak.authenticated || !this.keycloak.token) {
            return undefined;
        }

        const tokenParsed = this.keycloak.tokenParsed;
        return {
            token: this.keycloak.token,
            refreshToken: this.keycloak.refreshToken,
            username: tokenParsed?.preferred_username,
            email: tokenParsed?.email,
            roles: tokenParsed?.realm_access?.roles || [],
        };
    }

    onAuthStateChanged(callback: (user: User | undefined) => void): () => void {
        this.authListeners.push(callback);

        // Return a function to unsubscribe
        return () => {
            this.authListeners = this.authListeners.filter((listener) => listener !== callback);
        };
    }

    onInitializationChanged(callback: (initialized: boolean) => void): () => void {
        this.initListeners.push(callback);

        // Return a function to unsubscribe
        return () => {
            this.initListeners = this.initListeners.filter((listener) => listener !== callback);
        };
    }

    // Notify all listeners about the authentication state change
    private notifyAuthListeners(): void {
        const user = this.getUser();
        this.authListeners.forEach((listener) => listener(user));
    }

    // Notify all listeners about the initialization state change
    private notifyInitListeners(): void {
        this.initListeners.forEach((listener) => listener(this.initialized));
    }

    // Error handling methods
    onError(callback: (error: AuthError) => void): () => void {
        this.errorListeners.push(callback);
        return () => {
            this.errorListeners = this.errorListeners.filter((listener) => listener !== callback);
        };
    }

    private notifyErrorListeners(error: AuthError): void {
        this.errorListeners.forEach((listener) => listener(error));
    }

    /**
     * Build debug information object for Keycloak errors
     * @param error The error object or data from Keycloak
     */
    private buildKeycloakDebugInfo(error?: any): any {
        const keycloakAny = this.keycloak as any; // Type assertion to access undocumented properties

        return {
            error,
            keycloakError: keycloakAny.error || "--",
            keycloakErrorDescription: keycloakAny.errorDescription || "--",
            keycloakProperties: Object.keys(this.keycloak), // Show available properties
            config: {
                url: this.keycloak.authServerUrl,
                realm: this.keycloak.realm,
                clientId: this.keycloak.clientId,
            },
            location: window.location.href,
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString(),
        };
    }

    // Handle API response errors
    async handleApiError(response: Response, context = "API call"): Promise<{ handled: boolean; error?: any }> {
        if (response.status === 401) {
            try {
                const errorData = await response.json();

                // Check if this is a token-related error that requires reauthentication
                if (
                    errorData.requiresReauth ||
                    (typeof errorData.errorCode === "string" && errorData.errorCode?.startsWith("AUTH_"))
                ) {
                    this.showReauthError(errorData);
                    return { handled: true, error: errorData };
                }
            } catch (jsonError) {
                // If we can't parse JSON, still treat as auth error
                this.showReauthError({
                    error: "Authentication failed",
                    errorCode: "AUTH_UNKNOWN",
                });
                return { handled: true, error: { error: jsonError, info: "unknown error" } };
            }
        }

        // For other errors, return unhandled so component can handle them
        return { handled: false };
    }

    // Show reauthentication error
    private showReauthError(errorData: any): void {
        const authError: AuthError = {
            type: "auth",
            title: i18n.t("auth:errors.authenticationRequired"),
            message: this.getAuthErrorMessage(errorData.errorCode),
            errorCode: errorData.errorCode,
            details: errorData.details,
            requiresReauth: true,
        };

        this.notifyErrorListeners(authError);
    }

    // Get user-friendly error message based on error code
    private getAuthErrorMessage(errorCode?: string): string {
        switch (errorCode) {
            case "AUTH_TOKEN_EXPIRED":
                return i18n.t("auth:errors.authTokenExpired");
            case "AUTH_TOKEN_MALFORMED":
                return i18n.t("auth:errors.authTokenMalformed");
            case "AUTH_TOKEN_INVALID_SIGNATURE":
                return i18n.t("auth:errors.authTokenInvalidSignature");
            case "AUTH_NO_TOKEN":
                return i18n.t("auth:errors.authNoToken");
            default:
                return i18n.t("auth:errors.authDefault");
        }
    }

    // Perform reauthentication
    async reauthenticate(): Promise<boolean> {
        try {
            // Try to refresh the token first
            const refreshed = await this.refreshToken();
            if (refreshed) {
                // Token refreshed successfully, reload the page
                window.location.reload();
                return true;
            } else {
                // Refresh failed, redirect to login
                await this.login();
                return false;
            }
        } catch (error) {
            logger.error("Reauthentication failed:", error);
            // Fallback to login
            await this.login();
            return false;
        }
    }
}
